import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@clarix/auth-store';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { IS_SINGLE_HOST } from '@/config/env';
import { LoginPage } from './LoginPage';
import { WorkspacePickerPage } from './WorkspacePickerPage';

/**
 * What `/login` means depends on where you are.
 *
 *   {slug}.clarix.sarvadhi.work/login  → the sign-in form
 *   clarix.sarvadhi.work/login         → "which workspace?"
 *
 * ── Why one path and not two ────────────────────────────────────────────────
 * `/login` is hard-coded in several places that must keep working: the logout
 * redirect (core/api/auth.api.ts), the 401 handler and its `onAuthPage`
 * allowlist (packages/api-client/src/client.ts), and every `?next=` bounce from
 * RequireAuth. Introducing a separate picker path would have meant chasing all
 * of them and would still leave any link a user had bookmarked landing on a form
 * that cannot work. Switching on the host instead means the apex simply asks the
 * missing question, and every existing redirect stays correct.
 *
 * ── Single-host ─────────────────────────────────────────────────────────────
 * With no per-workspace origin (local dev, VITE_SINGLE_HOST=true) the slug lives
 * in the session instead of the hostname. The picker writes it there and sends
 * the user back here, so the second visit renders the form. Without this branch
 * local dev could never reach a login form at all, since getWorkspaceSlug() is
 * always null there.
 *
 * ── The stale-slug trap, and the explicit escape hatch ──────────────────────
 * Once ANY slug has ever been set in single-host mode, every later /login visit
 * renders the form directly and never shows the picker again — by design, so a
 * timed-out session doesn't force re-typing the workspace. But that means there
 * is no field anywhere to type a DIFFERENT slug; the only way back is noticing
 * and clicking LoginPage's small "Change workspace" link. `?workspace=<slug>`
 * is the explicit override for everyone else: present on the URL, it wins over
 * whatever is already in the session — cleared and replaced BEFORE the
 * picker-vs-form decision below, the same "explicit intent wins" rule this page
 * already applies to `?email=`/`?password=`.
 */
export function LoginRoute() {
  const [params] = useSearchParams();
  const workspaceParam = params.get('workspace')?.trim().toLowerCase() || null;
  const setWorkspaceSlug = useAuthStore((s) => s.setWorkspaceSlug);
  // Applied once per param value, not on every render — this route re-renders
  // on every store update, including the one this effect itself causes.
  const appliedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!IS_SINGLE_HOST || !workspaceParam || appliedFor.current === workspaceParam) return;
    appliedFor.current = workspaceParam;
    setWorkspaceSlug(workspaceParam);
  }, [workspaceParam, setWorkspaceSlug]);

  const sessionSlug = useActiveWorkspaceSlug();
  // While the override effect above is still in flight (same tick, before the
  // store update is visible here), trust the param directly so this render
  // doesn't flash the picker for a slug that's already being applied.
  const slug = IS_SINGLE_HOST && workspaceParam ? workspaceParam : sessionSlug;

  return slug ? <LoginPage /> : <WorkspacePickerPage />;
}
